import {
  type CheckProviderSessionReq,
  type CheckProviderSessionRes,
  type SyncBillingByOrgIdWithPaymentProviderReq,
  type SyncBillingByOrgIdWithPaymentProviderRes,
} from '../entity/api/routes/organization';
import {
  type AbortDowngradeReq,
  type AbortDowngradeRes,
  type CancelSubscriptionReq,
  type CancelSubscriptionRes,
  type GetBillingByOrgIdReq,
  type GetBillingByOrgIdRes,
  type RequestBillingPortalUrlReq,
  type RequestBillingPortalUrlRes,
  type SubscribeSubscriptionReq,
  type SubscribeSubscriptionRes,
  type UncancelSubscriptionReq,
  type UncancelSubscriptionRes,
  type UpdateReq,
  type UpdateRes,
  cancelSubscriptionReqSchema,
  subscribeSubscriptionReqSchema,
} from '../entity/api/routes/organization-billing';
import {
  type BuyCreditsRes,
  type PlansListRes,
  plansListReqSchema,
} from '../entity/api/routes/subscription';
import { apiRequest, setURL } from './request';

export function getOrgBillingStatus(organizationId: GetBillingByOrgIdReq['params']['id']) {
  return apiRequest<GetBillingByOrgIdRes>(
    `/organization/${organizationId}/billing-status`,
    'GET',
    true
  );
}

export function syncBillingByOrgIdWithPaymentProvider(
  organizationId: SyncBillingByOrgIdWithPaymentProviderReq['params']['id']
) {
  return apiRequest<SyncBillingByOrgIdWithPaymentProviderRes>(
    `/organization/${organizationId}/billing-status/sync`,
    'POST',
    true
  );
}

export function checkProviderSession(
  organizationId: CheckProviderSessionReq['params']['id'],
  sessionId: CheckProviderSessionReq['body']['sessionId']
) {
  return apiRequest<CheckProviderSessionRes>(
    `/organization/${organizationId}/billing-status/check-session`,
    'POST',
    true,
    { sessionId }
  );
}

export function getPlansData(organizationId?: string): Promise<PlansListRes> {
  const req = plansListReqSchema.parse({
    params: null,
    query: { oid: organizationId },
    body: null,
  });

  const url = setURL(`/subscription/plans`, req.query?.oid == null ? undefined : req.query);
  return apiRequest<PlansListRes>(url, 'GET', true);
}

export function cancelSubscription(
  organizationId: CancelSubscriptionReq['params']['organizationId'],
  data: CancelSubscriptionReq['body']
): Promise<CancelSubscriptionRes> {
  const req = cancelSubscriptionReqSchema.parse({
    params: { organizationId },
    query: null,
    body: data,
  });

  return apiRequest<CancelSubscriptionRes>(
    `subscription/organization/${organizationId}/cancel`,
    'POST',
    true,
    req.body
  );
}

export function unCancelSubscription(
  organizationId: UncancelSubscriptionReq['params']['organizationId']
): Promise<UncancelSubscriptionRes> {
  return apiRequest<UncancelSubscriptionRes>(
    `subscription/organization/${organizationId}/uncancel`,
    'POST',
    true
  );
}

export function updateSubscription(
  organizationId: UpdateReq['params']['organizationId'],
  planId: UpdateReq['body']['planId'],
  discountCoupon: UpdateReq['body']['discountCoupon']
): Promise<UpdateRes> {
  return apiRequest<UpdateRes>(`subscription/organization/${organizationId}/update`, 'POST', true, {
    planId,
    discountCoupon,
  });
}

export function subscribeOrganization(
  organizationId: SubscribeSubscriptionReq['params']['organizationId'],
  planId: SubscribeSubscriptionReq['body']['planId'],
  toltReferral: SubscribeSubscriptionReq['body']['toltReferral']
): Promise<SubscribeSubscriptionRes> {
  const req = subscribeSubscriptionReqSchema.parse({
    params: { organizationId },
    query: null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    body: { planId, toltReferral },
  });

  return apiRequest<SubscribeSubscriptionRes>(
    `subscription/organization/${organizationId}/subscribe`,
    'POST',
    true,
    req.body
  );
}

export function requestBillingPortalUrl(
  id: RequestBillingPortalUrlReq['params']['organizationId']
) {
  return apiRequest<RequestBillingPortalUrlRes>(`/organization/${id}/billing-portal`, 'GET', true);
}

export function abortDowngrade(
  organizationId: AbortDowngradeReq['params']['organizationId']
): Promise<AbortDowngradeRes> {
  return apiRequest<AbortDowngradeRes>(
    `subscription/organization/${organizationId}/abort-downgrade`,
    'POST',
    true
  );
}

export function buyExtraCredits(
  organizationId: string,
  creditCount: number
): Promise<BuyCreditsRes> {
  return apiRequest<BuyCreditsRes>(
    `subscription/organization/${organizationId}/buy-credits`,
    'POST',
    true,
    { creditCount }
  );
}
